import React, { useCallback, useEffect, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";

// Modal that fetches and shows the applicant's funding-account details
// (IBAN, BIC, Account ID, Currency) from Faisal's /getaccountdetails
// endpoint. These are NOT secrets — no passkey required, no auto-hide
// countdown. Users just want the IBAN to receive incoming transfers.

const parseAccountDetails = (response) => {
  // Faisal's /getaccountdetails envelope puts the fields DIRECTLY on
  // `payload` (not wrapped in a `data` block the way most other Orenda
  // endpoints do), so try payload first, then the usual nested paths,
  // then errorMessage as a last resort.
  const candidates = [
    response?.payload,
    response?.payload?.data,
    response?.payload?.data?.data,
    response?.data,
    response?.data?.data,
  ].filter(Boolean);
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      candidates.push(parsed);
      if (parsed?.data) candidates.push(parsed.data);
    } catch { /* ignore */ }
  }
  const pick = (...keys) => {
    for (const source of candidates) {
      for (const k of keys) {
        const v = source[k];
        if (v !== null && v !== undefined && v !== "") return v;
      }
    }
    return null;
  };
  return {
    accountId: pick("AccountId", "accountId"),
    tokenId: pick("TokenId", "tokenId"),
    customerId: pick("CustomerId", "customerId"),
    bic: pick("Bic", "BIC", "bic"),
    iban: pick("IBAN", "Iban", "iban"),
    currency: pick("Currency", "currency"),
    status: pick("Status", "status"),
  };
};

// Normalise whatever Faisal's backend emits into a crisp two-state verdict.
// "active" → account is live. "suspend" / "suspended" / "blocked" → account
// is paused. Anything we don't recognise falls through to "unknown" and the
// button block is hidden so we never offer an action we aren't sure about.
const normaliseStatus = (raw) => {
  if (!raw) return "unknown";
  const s = String(raw).trim().toLowerCase();
  if (s === "active") return "active";
  if (s === "suspend" || s === "suspended" || s === "blocked" || s === "block")
    return "suspended";
  return "unknown";
};

const OrendaAccountDetails = ({ open, onClose }) => {
  const [phase, setPhase] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  // Block / unblock confirm dialog state. `confirmAction` is "block" or
  // "unblock" (null when the dialog is hidden). `actionError` carries any
  // backend error from the submit; `actionBusy` disables the submit button.
  const [confirmAction, setConfirmAction] = useState(null);
  const [reason, setReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const dialogRef = useRef(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setError(null);
    setDetails(null);
    setCopiedKey(null);
    setConfirmAction(null);
    setReason("");
    setActionBusy(false);
    setActionError(null);
  }, []);

  const runFetch = useCallback(async () => {
    setError(null);
    setDetails(null);
    setPhase("loading");
    try {
      const resp = await orendaService.getAccountDetailsOrenda();
      const parsed = parseAccountDetails(resp);
      if (!parsed?.accountId && !parsed?.iban) {
        throw new Error("No account details were returned by the server.");
      }
      setDetails(parsed);
      setPhase("ready");
    } catch (err) {
      if (err?.code === "SSO_SESSION_EXPIRED") {
        setError("Your session expired. Please sign in again.");
      } else {
        setError(
          err?.response?.data?.errorMessage || err?.message || String(err)
        );
      }
      setPhase("error");
    }
  }, []);

  // Submit a block or unblock on the current account. The confirm dialog
  // routes to this; we then refetch so the status flips without a reload.
  const submitBlockUnblock = useCallback(async () => {
    if (!confirmAction) return;
    setActionError(null);
    setActionBusy(true);
    try {
      await orendaService.blockUnblockAccountOrenda({
        isBlock: confirmAction === "block",
        reason: reason.trim(),
      });
      setActionBusy(false);
      setConfirmAction(null);
      setReason("");
      // Refetch so status flips from active → suspended (or back).
      await runFetch();
    } catch (err) {
      setActionBusy(false);
      setActionError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "The action couldn't complete. Please try again."
      );
    }
  }, [confirmAction, reason, runFetch]);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    runFetch();
  }, [open, reset, runFetch]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const copy = (key, value) => {
    if (!value) return;
    try {
      navigator.clipboard.writeText(String(value).replace(/\s+/g, ""));
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div
      className="orenda-reveal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orenda-account-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="orenda-reveal" ref={dialogRef}>
        <div className="orenda-reveal-head">
          <div>
            <div className="orenda-reveal-eyebrow">Account details</div>
            <h2 id="orenda-account-title" className="orenda-reveal-title">
              Your account
            </h2>
          </div>
          <button
            type="button"
            className="orenda-reveal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="orenda-reveal-body">
          {phase === "loading" && (
            <Spinner label="Fetching your account details…" />
          )}

          {phase === "ready" && details && (
            <>
              <FieldRow
                label="IBAN"
                value={details.iban}
                mono
                copied={copiedKey === "iban"}
                onCopy={() => copy("iban", details.iban)}
              />
              <div className="orenda-reveal-grid">
                <FieldRow
                  label="BIC / SWIFT"
                  value={details.bic}
                  mono
                  copied={copiedKey === "bic"}
                  onCopy={() => copy("bic", details.bic)}
                  smallGap
                />
                <FieldRow
                  label="Currency"
                  value={details.currency}
                  mono
                  smallGap
                />
              </div>
              <FieldRow
                label="Account ID"
                value={details.accountId}
                mono
                copied={copiedKey === "accountId"}
                onCopy={() => copy("accountId", details.accountId)}
              />
              <p className="orenda-reveal-countdown">
                Share these details with payers who need to send you money.
              </p>

              {/* Block / unblock surfaces based on the account's current
                  status. We only render an action when we're confident
                  about the state — if status is "unknown" we show nothing
                  rather than offer the wrong action. */}
              {normaliseStatus(details.status) === "active" && (
                <div className="orenda-account-actions">
                  <button
                    type="button"
                    className="orenda-btn-danger"
                    onClick={() => {
                      setActionError(null);
                      setReason("");
                      setConfirmAction("block");
                    }}
                  >
                    Block account
                  </button>
                </div>
              )}
              {normaliseStatus(details.status) === "suspended" && (
                <div className="orenda-account-actions">
                  <button
                    type="button"
                    className="orenda-btn-primary"
                    onClick={() => {
                      setActionError(null);
                      setReason("");
                      setConfirmAction("unblock");
                    }}
                  >
                    Unblock account
                  </button>
                </div>
              )}
            </>
          )}

          {phase === "error" && (
            <div className="orenda-reveal-error">
              <h3 className="orenda-reveal-expired-title">
                Couldn't load account details
              </h3>
              <p className="orenda-reveal-expired-sub">{error}</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="orenda-btn-primary"
                  onClick={runFetch}
                >
                  Try again
                </button>
                <button
                  type="button"
                  className="orenda-btn-ghost"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirm overlay for block / unblock. Mounted inside the backdrop
          so Esc + outside-click still land on the parent close handler. */}
      {confirmAction && (
        <div
          className="orenda-account-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="orenda-account-confirm-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="orenda-account-confirm-card">
            <h3
              id="orenda-account-confirm-title"
              className="orenda-reveal-expired-title"
            >
              {confirmAction === "block"
                ? "Block this account?"
                : "Unblock this account?"}
            </h3>
            <p className="orenda-reveal-expired-sub">
              {confirmAction === "block"
                ? "Incoming payments will be rejected and card use will be paused until the account is unblocked."
                : "Incoming payments and card use will resume as soon as the account is unblocked."}
            </p>
            <label
              htmlFor="orenda-account-reason"
              className="orenda-reveal-field-label"
              style={{ display: "block", marginTop: 8 }}
            >
              Reason (optional)
            </label>
            <textarea
              id="orenda-account-reason"
              className="orenda-account-reason-input"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder={
                confirmAction === "block"
                  ? "e.g. Lost phone, suspected fraud…"
                  : "e.g. Verified identity, resuming activity…"
              }
              disabled={actionBusy}
              maxLength={500}
            />
            {actionError && (
              <div
                className="orenda-reveal-countdown"
                style={{ color: "#c04040", marginTop: 8 }}
              >
                {actionError}
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 16,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="orenda-btn-ghost"
                onClick={() => {
                  setConfirmAction(null);
                  setActionError(null);
                }}
                disabled={actionBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  confirmAction === "block"
                    ? "orenda-btn-danger"
                    : "orenda-btn-primary"
                }
                onClick={submitBlockUnblock}
                disabled={actionBusy}
              >
                {actionBusy
                  ? "Submitting…"
                  : confirmAction === "block"
                  ? "Yes, block it"
                  : "Yes, unblock it"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FieldRow = ({ label, value, mono, onCopy, copied, smallGap }) => (
  <div className={`orenda-reveal-field${smallGap ? " orenda-reveal-field--sm" : ""}`}>
    <div className="orenda-reveal-field-label">{label}</div>
    <div className="orenda-reveal-field-row">
      <div
        className={`orenda-reveal-field-value${mono ? " orenda-reveal-field-value--mono" : ""}`}
      >
        {value || "—"}
      </div>
      {onCopy && value && (
        <div className="orenda-reveal-field-actions">
          <button
            type="button"
            className="orenda-reveal-icon-btn"
            onClick={onCopy}
            title="Copy to clipboard"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
    </div>
  </div>
);

const Spinner = ({ label }) => (
  <div className="orenda-reveal-spinner">
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-label">{label}</div>
  </div>
);

export default OrendaAccountDetails;
